import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Eye, EyeOff } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import {
  LoginFormValues,
  loginInitialValues,
  loginValidationSchema,
} from '@/pages/Login/validation'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux/store'
import { fetchUserData, userLogin } from '@/redux/asyncThunks/user'
import { useAppSelector } from '@/redux/hooks'
import { UserRole } from '@/constants/room'
import { resetError } from '@/redux/slices/userSlice'
import ROUTES from '@/routes/routes'

const LogIn: React.FC = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { loading, error, user } = useAppSelector((state) => state.user)

  useEffect(() => {
    dispatch(resetError())
  }, [dispatch])

  useEffect(() => {
    if (user) {
      if (user.role === UserRole.Student) {
        navigate(ROUTES.STUDENT.DASHBOARD, { replace: true })
      } else {
        navigate(ROUTES.PROFESSOR.ROOMS, { replace: true })
      }
    }
  }, [user, navigate])

  const handleSubmit = async (values: LoginFormValues) => {
    try {
      await dispatch(userLogin(values)).unwrap()
      await dispatch(fetchUserData()).unwrap()
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  return (
    <div className='flex h-screen w-screen flex-col md:flex-row'>
      <div className='relative hidden flex-1 md:block'>
        <img
          src='/images/auth-background.svg'
          alt='Auth background'
          className='absolute w-1/2 object-cover md:h-full md:w-5/6'
        />
        <div className='flex h-full items-center justify-center'>
          <img
            src='/images/auth-avatar.svg'
            alt='Auth Avatar'
            className='absolute z-10 object-contain md:h-full md:w-auto'
          />
        </div>
      </div>
      <div className='flex flex-1 flex-col items-center justify-center'>
        <div className='flex justify-center'>
          <img
            src='/images/oge.svg'
            alt='Huble logo'
            className='w-1/2 object-contain md:w-full'
          />
        </div>
        <div className='flex w-3/4 flex-col items-center md:w-full'>
          <Card className='w-full max-w-md border-none shadow-none'>
            <CardHeader className='space-y-1 flex flex-col items-center'>
              <CardTitle className='text-4xl font-bold leading-[50px]'>
                Welcome to OGE
              </CardTitle>
              <CardTitle className='text-3xl text-[#023572] font-bold leading-10'>
                Login
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Formik
                initialValues={loginInitialValues}
                validationSchema={loginValidationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  isSubmitting,
                }) => (
                  <Form className='space-y-4'>
                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.email && errors.email
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='email'
                      >
                        Email
                      </Label>
                      <Input
                        className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                          touched.email && errors.email
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                        id='email'
                        name='email'
                        type='email'
                        placeholder='Enter an Email'
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.email && errors.email && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.email}
                        </div>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.password && errors.password
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='password'
                      >
                        Password
                      </Label>
                      <div className='relative'>
                        <Input
                          className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                            touched.password && errors.password
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                          id='password'
                          name='password'
                          type={showPassword ? 'text' : 'password'}
                          placeholder='At least 8 characters'
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <Eye className='h-4 w-4 text-gray-400' />
                          ) : (
                            <EyeOff className='h-4 w-4 text-gray-400' />
                          )}
                        </Button>
                      </div>
                      {touched.password && errors.password && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.password}
                        </div>
                      )}
                      <div className='justify-end hidden'>
                        <Link
                          className='text-base text-[#023572]'
                          to='/forgot-password'
                        >
                          Forgot Password?
                        </Link>
                      </div>
                    </div>
                    {error && (
                      <div className='text-red-500 text-sm flex gap-2'>
                        <img src='/icons/alert-circle.svg' alt='Warning' />
                        {typeof error == 'object' ? (
                          <>
                            {Object.values(error).map((_error, index) => (
                              <p key={index}>{_error as string}</p>
                            ))}
                          </>
                        ) : (
                          error
                        )}
                      </div>
                    )}
                    <Link
                      to='/forgot-password'
                      className='text-sm text-[#023572] hover:text-blue-800'
                    >
                      Forgot Password?
                    </Link>
                    <Button
                      type='submit'
                      disabled={loading || isSubmitting}
                      className='w-full h-[38px] text-base font-normal bg-dark-blue hover:bg-dark-blue-hovered'
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>

                    <div className='text-sm text-center'>
                      <span className='text-[#122B31] text-lg'>
                        Don&apos;t you have an account?{' '}
                      </span>
                      <Link
                        to='/register'
                        className='text-lg text-[#023572] hover:text-blue-800'
                      >
                        Sign Up
                      </Link>
                    </div>
                    <div className='mt-1'>
                      <p className='text-sm text-center'>
                        By clicking continue, you agree to our Terms of Service
                        and Privacy Policy.
                      </p>
                    </div>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default LogIn
